import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { EyeIcon } from '../../../components/icons/Icons';

// This component is the dedicated view for the "All Reports" tab
export default function AllReportsView({ appId, onViewReport, approversMap }) {
    const [allReports, setAllReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Fetch ALL reports, ordered by creation date
    useEffect(() => {
        if (!appId) {
            setIsLoading(false);
            return;
        }
        const reportsCollectionPath = `/artifacts/${appId}/public/data/expenseReports`;
        const q = query(
            collection(db, reportsCollectionPath),
            orderBy("createdAt", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedReports = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setAllReports(fetchedReports);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [appId]);
    
    // Memo for search and sort
    const processedReports = useMemo(() => {
        let filtered = [...allReports];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(report =>
                report.reportName?.toLowerCase().includes(lowercasedTerm) ||
                report.reportNumber?.toLowerCase().includes(lowercasedTerm) ||
                report.requesterName?.toLowerCase().includes(lowercasedTerm) ||
                approversMap.get(report.managerId)?.toLowerCase().includes(lowercasedTerm)
            );
        }
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            
            if (sortConfig.key === 'managerId') {
                aValue = approversMap.get(a.managerId) || '';
                bValue = approversMap.get(b.managerId) || '';
            }
            
            if (aValue?.toDate) aValue = aValue.toDate();
            if (bValue?.toDate) bValue = bValue.toDate();
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [allReports, searchTerm, sortConfig, approversMap]);

    // Memo for pagination
    const paginatedReports = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return processedReports.slice(startIndex, startIndex + itemsPerPage);
    }, [processedReports, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(processedReports.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };
    
    // Status badge logic
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Pending Approval': return 'bg-yellow-100 text-yellow-800';
            case 'Approved': return 'bg-green-100 text-green-800';
            case 'Rejected':
            case 'Rejected by Finance': 
		        return 'bg-red-100 text-red-800';
            case 'Reimbursed': return 'bg-blue-100 text-blue-800';
            case 'Draft': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search all reports by ID, submitter, approver, or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                />
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('reportNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report ID</th>
                                <th onClick={() => requestSort('requesterName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Submitter</th>
                                <th onClick={() => requestSort('managerId')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Approver</th>
                                <th onClick={() => requestSort('totalAmount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Amount</th>
                                <th onClick={() => requestSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Submission Date</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedReports.map(report => (
                                <tr key={report.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{report.reportNumber}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{report.requesterName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{approversMap.get(report.managerId) || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{report.reimbursementCurrency} {report.totalAmount.toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{report.createdAt?.toDate().toLocaleDateString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(report.status)}`}>
                                            {report.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button onClick={() => onViewReport(report)} className="text-gray-500 hover:text-blue-600" title="View Details"><EyeIcon /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">Page {currentPage} of {totalPages}</span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}